/**
 * Editor draft for an automatic discount (F-DEAL-N3) and its mapping to and
 * from the API shape. Pure, so it is unit-tested.
 */
import type { AutoDiscountAdmin, AutoDiscountInput } from '@/types/deals'

export type AutoKind = AutoDiscountInput['kind']
export type RewardType = 'percent' | 'amount' | 'free_item'

export interface AutoDiscountDraft {
  name: string
  kind: AutoKind
  is_active: boolean
  // buy X get Y
  buy_qty: string
  get_qty: string
  get_percent: string
  get_mode: 'same' | 'different'
  // bundle
  bundle_qty: string
  bundle_price: string
  // spend over
  min_subtotal: string
  reward_type: RewardType
  reward_value: string
  max_discount: string
  reward_product_id: string
  // targets
  buy_product_ids: string[]
  buy_category_ids: string[]
  get_product_ids: string[]
  get_category_ids: string[]
  // when
  days: number[]
  timed: boolean
  start_minute: number | null
  end_minute: number | null
  valid_from: string
  valid_to: string
  till: boolean
  online: boolean
  order_types: string[]
}

export function emptyDraft(kind: AutoKind = 'buy_x_get_y'): AutoDiscountDraft {
  return {
    name: '',
    kind,
    is_active: true,
    buy_qty: '1',
    get_qty: '1',
    get_percent: '100',
    get_mode: 'same',
    bundle_qty: '2',
    bundle_price: '',
    min_subtotal: '',
    reward_type: 'percent',
    reward_value: '',
    max_discount: '',
    reward_product_id: '',
    buy_product_ids: [],
    buy_category_ids: [],
    get_product_ids: [],
    get_category_ids: [],
    days: [],
    timed: false,
    start_minute: 12 * 60,
    end_minute: 16 * 60,
    valid_from: '',
    valid_to: '',
    till: true,
    online: true,
    order_types: [],
  }
}

const str = (n: number | null | undefined) => (n == null || n === 0 ? '' : String(n))

export function draftFromRule(r: AutoDiscountAdmin): AutoDiscountDraft {
  const chans = (r.channels ?? []).map((c) => c.toLowerCase())
  const timed = r.start_minute != null && r.end_minute != null
  const different = (r.get_product_ids?.length ?? 0) > 0 || (r.get_category_ids?.length ?? 0) > 0
  return {
    name: r.name,
    kind: r.kind,
    is_active: r.is_active,
    buy_qty: String(r.buy_qty || 1),
    get_qty: String(r.get_qty || 1),
    get_percent: String(r.get_percent || 100),
    get_mode: different ? 'different' : 'same',
    bundle_qty: String(r.bundle_qty || 2),
    bundle_price: r.kind === 'multi_buy' ? String(r.bundle_price ?? '') : '',
    min_subtotal: str(r.min_subtotal),
    reward_type: r.reward_type === 'amount' || r.reward_type === 'free_item' ? r.reward_type : 'percent',
    reward_value: str(r.reward_value),
    max_discount: str(r.max_discount),
    reward_product_id: r.reward_product_id ?? '',
    buy_product_ids: [...(r.buy_product_ids ?? [])],
    buy_category_ids: [...(r.buy_category_ids ?? [])],
    get_product_ids: [...(r.get_product_ids ?? [])],
    get_category_ids: [...(r.get_category_ids ?? [])],
    days: [...(r.days ?? [])],
    timed,
    start_minute: timed ? r.start_minute : 12 * 60,
    end_minute: timed ? r.end_minute : 16 * 60,
    valid_from: r.valid_from ?? '',
    valid_to: r.valid_to ?? '',
    till: chans.length === 0 || chans.includes('till'),
    online: chans.length === 0 || chans.includes('online'),
    order_types: [...(r.order_types ?? [])],
  }
}

const num = (s: string): number | null => {
  if (s == null || String(s).trim() === '') return null
  const n = Number(s)
  return Number.isFinite(n) ? n : null
}

/** Client-side checks that mirror the server; null = OK to send. */
export function draftProblem(d: AutoDiscountDraft): string | null {
  if (!d.name.trim()) return 'Give the deal a name'
  if (!d.till && !d.online) return 'Pick at least one place it runs: the till or online'
  const hasBuy = d.buy_product_ids.length + d.buy_category_ids.length > 0
  switch (d.kind) {
    case 'buy_x_get_y': {
      const b = num(d.buy_qty)
      const g = num(d.get_qty)
      const p = num(d.get_percent)
      if (b == null || b < 1) return 'Buy quantity must be 1 or more'
      if (g == null || g < 1) return 'Free quantity must be 1 or more'
      if (p == null || p <= 0 || p > 100) return 'The discount on the extra item must be between 1% and 100%'
      if (!hasBuy) return 'Pick the items or categories the deal is on'
      if (d.get_mode === 'different' && d.get_product_ids.length + d.get_category_ids.length === 0)
        return 'Pick what the extra item can be'
      break
    }
    case 'multi_buy': {
      const q = num(d.bundle_qty)
      const p = num(d.bundle_price)
      if (q == null || q < 2) return 'Bundle quantity must be 2 or more'
      if (p == null || p < 0) return 'Set the bundle price'
      if (!hasBuy) return 'Pick the items or categories the deal is on'
      break
    }
    case 'spend_over': {
      const m = num(d.min_subtotal)
      if (m == null || m <= 0) return 'Set the minimum spend'
      if (d.reward_type === 'percent') {
        const v = num(d.reward_value)
        if (v == null || v <= 0 || v > 100) return 'The percentage must be between 1 and 100'
      } else if (d.reward_type === 'amount') {
        const v = num(d.reward_value)
        if (v == null || v <= 0) return 'Set the amount off'
      } else if (!d.reward_product_id) return 'Pick the free item'
      break
    }
  }
  if (d.timed) {
    if (d.start_minute == null || d.end_minute == null) return 'Set both a start and an end time'
    if (d.start_minute === d.end_minute) return 'Start and end time cannot be the same'
  }
  if (d.valid_from && d.valid_to && d.valid_from > d.valid_to) return 'The end date is before the start date'
  return null
}

/** The POST/PUT body. Only the fields of the chosen kind are sent. */
export function draftToInput(d: AutoDiscountDraft): AutoDiscountInput {
  const out: AutoDiscountInput = {
    name: d.name.trim(),
    kind: d.kind,
    is_active: d.is_active,
    buy_product_ids: d.buy_product_ids,
    buy_category_ids: d.buy_category_ids,
    get_product_ids: [],
    get_category_ids: [],
    order_types: d.order_types,
    channels: d.till && d.online ? [] : d.till ? ['till'] : ['online'],
    days: d.days.length === 7 ? [] : d.days,
    start_minute: d.timed ? d.start_minute : null,
    end_minute: d.timed ? d.end_minute : null,
    valid_from: d.valid_from || null,
    valid_to: d.valid_to || null,
  }
  switch (d.kind) {
    case 'buy_x_get_y':
      out.buy_qty = num(d.buy_qty)
      out.get_qty = num(d.get_qty)
      out.get_percent = num(d.get_percent)
      if (d.get_mode === 'different') {
        out.get_product_ids = d.get_product_ids
        out.get_category_ids = d.get_category_ids
      }
      break
    case 'multi_buy':
      out.bundle_qty = num(d.bundle_qty)
      out.bundle_price = num(d.bundle_price)
      break
    case 'spend_over':
      out.min_subtotal = num(d.min_subtotal)
      out.reward_type = d.reward_type
      if (d.reward_type === 'free_item') out.reward_product_id = d.reward_product_id || null
      else out.reward_value = num(d.reward_value)
      if (d.reward_type !== 'amount') out.max_discount = num(d.max_discount)
      break
  }
  return out
}

/** A saved rule as a full PUT body (used to flip is_active from the list). */
export function ruleToInput(r: AutoDiscountAdmin, patch?: Partial<AutoDiscountInput>): AutoDiscountInput {
  return { ...draftToInput(draftFromRule(r)), ...patch }
}
